import type { SearchHit, SearchOrder } from '../../api/search'
import { DESK_AREA, pathToElement } from '../desk/place'
import { pathToAppointment, TIMETABLE_AREA } from '../timetable/place'
import { dayKey } from '../timetable/week'
import { TOP, pathTo as pathInLibrary } from '../library/place'

/**
 * Where the detailed search is, worked out from the address and nothing else.
 *
 * `#/search`, and below it the search line itself: `#/search/vertrag%20folder%3ASemester%203`. **The
 * whole search is one segment**, because the whole search is one string — the filters are written into
 * the line and are not separate parameters. So a link to a search is a link somebody can send, and the
 * browser's own back button walks out of it.
 *
 * The order is the one thing that is not part of the line, and deliberately so: it belongs to the person
 * looking rather than to the question, and putting it in the search language would mean somebody could
 * type a sort order into a search box and then wonder why their words had gone.
 */
export const SEARCH_AREA = 'search'

/** The two orders the detailed search offers. Relevance unless something else is asked for. */
export const ORDERS: readonly { readonly value: SearchOrder; readonly label: string }[] = [
  { value: 'relevance', label: 'Best hits' },
  { value: 'newest', label: 'Newest first' },
]

export interface Searched {
  readonly line: string
  readonly order: SearchOrder
}

/** What the address says is being searched for. */
export function searchedIn(path: readonly string[]): Searched {
  const [line, order] = path
  return {
    line: line ?? '',
    order: ORDERS.some((one) => one.value === order) ? (order as SearchOrder) : 'relevance',
  }
}

/**
 * The address of a search.
 *
 * The order is left out where it is the ordinary one, so that the address somebody copies out of the bar
 * is the shortest thing that says what they are looking at.
 */
export function pathTo(searched: Searched): string[] {
  if (searched.line === '') {
    return []
  }
  return searched.order === 'relevance' ? [searched.line] : [searched.line, searched.order]
}

/**
 * Where a hit lives, as an area and a path inside it.
 *
 * Worked out here rather than in each of the two screens that open one, because "a note opens on the
 * pad and a document opens in the library" is one sentence and would otherwise be written twice — and
 * the second kind of content is exactly where two copies start to disagree.
 *
 * `null` for a hit pointing at something neither screen knows how to open, which is what a newer
 * service talking to an older page looks like. Better to do nothing than to navigate somewhere wrong.
 */
export function whereItLives(hit: SearchHit): { area: string; path: string[] } | null {
  if (hit.boardElement !== null && hit.boardElement !== undefined) {
    // The board *and* the element: a hit on a surface leads to a place on it, not merely to the
    // surface. It is the same address the board's own "find on this board" writes.
    const on = hit.boardElement
    return { area: DESK_AREA, path: pathToElement(on.boardId, on.element.id) }
  }
  if (hit.note !== null && hit.note !== undefined) {
    return { area: 'notes', path: [hit.note.id] }
  }
  if (hit.task !== null && hit.task !== undefined) {
    return { area: 'tasks', path: [hit.task.id] }
  }
  if (hit.flashcard !== null && hit.flashcard !== undefined) {
    // The card itself, and not merely the area it is in: a hit that led to the writing surface was a
    // hit that lost what was found.
    return { area: 'cards', path: ['card', hit.flashcard.id] }
  }
  if (hit.subject !== null && hit.subject !== undefined) {
    return { area: 'subjects', path: [hit.subject.id] }
  }
  if (hit.appointment !== null && hit.appointment !== undefined) {
    /*
      The week the appointment is in, and then the appointment. The plan's address always carries the week —
      arriving on a lecture in November and then closing it must not land somebody on today.
    */
    const appointment = hit.appointment
    const day = dayKey(new Date(appointment.startsAt), appointment.zone)
    /*
      A hit on a write-up leads to the *session* half of the appointment, and one on the appointment
      itself to the appointment half. Both are the same page, and which of the two is open is in the
      address — so finding your own sentence again lands you where that sentence is rather than at a
      room number.
    */
    const tab = hit.kind === 'writeup' ? 'session' : 'appointment'
    return { area: TIMETABLE_AREA, path: pathToAppointment('week', day, appointment.id, tab) }
  }
  if (hit.document !== null && hit.document !== undefined) {
    const document = hit.document
    const place =
      document.folderId === null ? TOP : ({ kind: 'folder', id: document.folderId } as const)
    return { area: 'library', path: pathInLibrary(place, document.id) }
  }
  return null
}
